import React from 'react'
import { css } from '@emotion/react'
import { isSameDay, parseISO } from 'date-fns'
import dynamic from 'next/dynamic'
import { colors, font } from 'plume-ui'
import * as ReactDOMServer from 'react-dom/server'
import { formatDate } from '@/lib/date'
import { scrollToId } from '@/lib/page'
import { assertNever } from '@/lib/ts-utils'
import { CompanyChartData, CompanyChartEventData } from '../../../../types/api'
import { CustomTooltip } from './CustomTooltip'
const Chart = dynamic(() => import('react-apexcharts'), { ssr: false })

type Props = {
  data: CompanyChartData
  endDate: Date
}

export const HorizontalEventsChart: React.FC<Props> = (props) => {
  const series = seriesOf(props.data, props.endDate)

  const handleDataPointSelection = (
    _e: any,
    _ctx: any,
    {
      dataPointIndex,
      seriesIndex,
    }: { dataPointIndex: number; seriesIndex: number }
  ) => {
    const selectedSeries = seriesIndex === 0 ? 'events' : 'news'

    if (selectedSeries === 'events') {
      const selectedDataPoint = props.data.events[dataPointIndex - 1]

      if (selectedDataPoint.type === '住所変更') {
        return scrollToId('address-histories', { offset: 100 })
      } else if (selectedDataPoint.type === 'IPO') {
        return scrollToId('ipo-data')
      } else {
        window.open(selectedDataPoint.item.path)
      }
    } else {
      const selectedDataPoint = props.data.news[dataPointIndex - 1]

      if (selectedDataPoint.type === 'ファイナンス・M&A') {
        window.open(selectedDataPoint.item.path)
      } else {
        window.open(selectedDataPoint.item.url)
      }
    }
  }

  const hiddenMarkers = [
    {
      seriesIndex: 0,
      dataPointIndex: 0,
      size: 0,
    },
    {
      seriesIndex: 0,
      dataPointIndex: series[0].data.length - 1,
      size: 0,
    },
    {
      seriesIndex: 1,
      dataPointIndex: 0,
      size: 0,
    },
    {
      seriesIndex: 1,
      dataPointIndex: series[1].data.length - 1,
      size: 0,
    },
  ]

  const options = {
    chart: {
      type: 'line',
      zoom: { enabled: false },
      toolbar: { show: false },
      events: {
        dataPointSelection: handleDataPointSelection,
      },
    },
    stroke: { width: 0 },
    grid: {
      show: false,
      xaxis: {
        lines: {
          show: false,
        },
      },
    },
    xaxis: {
      type: 'datetime',
      labels: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    yaxis: {
      labels: {
        show: false,
      },
    },
    legend: {
      show: false,
    },
    markers: {
      size: 8,
      shape: 'square',
      colors: [colors.red50, colors.blueGray40],
      hover: {
        size: 8,
      },
      strokeWidth: 0,
      discrete: hiddenMarkers,
      fillOpacity: 0.6,
    },
    tooltip: {
      shared: false,
      intersect: true,
      enabledOnSeries: false,
      onDatasetHover: {
        highlightDataSeries: false,
      },
      custom: ({
        dataPointIndex,
        seriesIndex,
      }: {
        dataPointIndex: number
        seriesIndex: number
      }) => {
        const series = seriesIndex === 0 ? 'events' : 'news'
        return tooltipHTMLOf(props.data, series, dataPointIndex)
      },
    },
  }

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        margin-bottom: -20px;
      `}
    >
      <div
        css={css`
          font-size: 13px;
          line-height: 19px;
          font-weight: ${font.weight.bold};
          color: ${colors.text};
          margin-bottom: 22px;
        `}
      >
        <div>イベント</div>
        <div
          css={css`
            margin-top: 5px;
          `}
        >
          ニュース
        </div>
      </div>
      <div
        css={css`
          margin-bottom: 22px;
        `}
      >
        <Chart
          type="line"
          width={1000}
          height={80}
          series={series}
          options={options}
        />
      </div>
    </div>
  )
}

const seriesOf = (data: CompanyChartData, endDate: Date) => {
  const start = data.fundingRounds[0]?.date
  const end = endDate.toISOString()

  const eventDates = data.events
    .filter((item) => parseISO(item.date) <= endDate)
    .map((item) => item.date)
  const newsDates = data.news
    .filter((item) => parseISO(item.date) <= endDate)
    .map((item) => item.date)

  const eventsData = [start, ...eventDates, end].map((date) => [
    parseISO(date as string).getTime(),
    50,
  ])

  const newsData = [start, ...newsDates, end].map((date) => [
    parseISO(date as string).getTime(),
    30,
  ])

  return [
    {
      name: 'events',
      data: eventsData,
    },
    {
      name: 'news',
      data: newsData,
    },
  ]
}

const tooltipHTMLOf = (
  data: CompanyChartData,
  series: 'events' | 'news',
  targetIndex: number
) => {
  switch (series) {
    case 'events':
      const event = data.events.find((n) => {
        return isSameDay(
          parseISO(n.date),
          parseISO(data.events[targetIndex - 1]?.date)
        )
      })
      if (!event) {
        return null
      }
      return ReactDOMServer.renderToStaticMarkup(
        <CustomTooltip
          date={formatDate(event.date, 'yyyy/MM/dd')}
          items={[
            {
              title: event.type,
              text: eventTooltipText(event),
            },
          ]}
        />
      )
    case 'news':
      const news = data.news.find((n) => {
        return isSameDay(
          parseISO(n.date),
          parseISO(data.news[targetIndex - 1]?.date)
        )
      })
      if (!news) {
        return null
      }
      return ReactDOMServer.renderToStaticMarkup(
        <CustomTooltip
          date={formatDate(news.date, 'yyyy/MM/dd')}
          items={[{ title: news.type, text: news.item.title }]}
        />
      )
  }
}

const eventTooltipText = (e: CompanyChartEventData) => {
  if (e.type === '住所変更') {
    return e.item.address
  }
  if (e.type === 'IPO') {
    return e.item.market
  }
  switch (e.type) {
    case '事業提携':
    case '事業譲受':
    case '事業譲渡':
    case '被買収・子会社化・主要株式取得':
      return e.item.roundDetails?.[0]?.gpInvestors?.[0]?.name ?? e.item.remarks
    case '合併（M&A,事業継承など）':
      return e.item.remarks
    case '創業':
    case '会社設立':
    case '破綻・清算・解散':
      return null
    default:
      return assertNever(e)
  }
}
